import { useState } from 'react'

import { toast } from 'react-toastify'

import IRemoteResource from '../interfaces/remote-resource'

import isHttpError from '../helpers/helper-http-error'

export const useResource = <T extends { id: number }>(
  remote: IRemoteResource<T>,
) => {
  const [resources, setResources] = useState<T[]>([])
  const [resource, setResource] = useState<T | null>(null)
  const [loading, setLoading] = useState(false)

  const load = async (params?: any) => {
    if (!remote.load) return null

    setLoading(true)

    try {
      const response = await remote.load(params)
      setResources(response.content)
      setLoading(false)
    } catch (error) {
      const httpError = isHttpError(error)

      if (httpError) {
        toast.error(httpError.message)
      } else {
        throw error
      }
    }

    return null
  }

  const loadById = async (id: number, params?: any) => {
    if (!remote.loadById) return null

    setLoading(true)

    try {
      const response = await remote.loadById(id, params)
      setResources([response as T, ...resources])
      setResource(response as T)
      setLoading(false)
      return response
    } catch (error) {
      const httpError = isHttpError(error)

      if (httpError) {
        toast.error(httpError.message)
      } else {
        throw error
      }
    }

    return null
  }

  const post = async (body: Partial<T>) => {
    if (!remote.post) return null

    setLoading(true)

    try {
      const response = await remote.post(body)
      setResources([response as T, ...resources])
      setLoading(false)
      return response
    } catch (error) {
      const httpError = isHttpError(error)

      if (httpError) {
        toast.error(httpError.message)
      } else {
        throw error
      }
    }

    return null
  }

  const update = async (id: number, body: Partial<T>) => {
    if (!remote.update) return null

    setLoading(true)

    try {
      const response = await remote.update(id, body)
      setResources((prevResource) => prevResource.map((item) => {
        if (item.id === id) {
          return { ...item, ...response.result } as T
        }
        return item
      }))
      setLoading(false)
    } catch (error) {
      const httpError = isHttpError(error)

      if (httpError) {
        toast.error(httpError.message)
      } else {
        throw error
      }
    }

    return null
  }

  const remove = async (id: number) => {
    if (!remote.delete) return null

    setLoading(true)

    try {
      await remote.delete(id)
      setResources((prevResource) => prevResource.filter((item) => item.id !== id))
      setLoading(false)
    } catch (error) {
      const httpError = isHttpError(error)

      if (httpError) {
        toast.error(httpError.message)
      } else {
        throw error
      }
    }

    return null
  }

  const service = {
    load,
    loadById,
    post,
    update,
    remove,
  }

  return [resources, service, resource, loading] as const
}
